import React from 'react';
import { Card, Descriptions, Spin, Tag } from 'antd';
import { FaBuilding, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import { formatDealerAmount } from '@/utils/currency';
import { ICustomer } from '../types';

// paymenttermscode = 2 → Credit Account
const CREDIT_TERMS_CREDIT = 2;

interface CustomerDetailsHeaderProps {
  customer: ICustomer | undefined;
  loading: boolean;
}

export const CustomerDetailsHeader: React.FC<CustomerDetailsHeaderProps> = ({
  customer,
  loading,
}) => {
  if (loading) {
    return (
      <Card>
        <div className="flex justify-center items-center py-8">
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!customer) {
    return (
      <Card>
        <div className="text-center py-8 text-gray-500">
          Customer not found
        </div>
      </Card>
    );
  }

  const getStatusColor = (statecode: number | undefined) => {
    if (statecode === 1) return 'red';
    if (statecode === 0) return 'green';
    return 'default';
  };

  const isCreditAccount = customer?.paymenttermscode === CREDIT_TERMS_CREDIT;
  const dealersCurrency = customer?.dogma_DealersCurrency;
  const exchangeRate = dealersCurrency?.dogma_exchangerate ?? 1;
  const formatAmount = (value: number) =>
    formatDealerAmount(value, dealersCurrency, exchangeRate);

  return (
    <Card>
      <div className="space-y-4">
        {/* Customer Name Header */}
        <div className="flex items-center gap-3 pb-4 border-b">
          <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center">
            <FaBuilding className="text-primary-500" size={24} />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-semibold text-gray-900">{customer.name}</h2>
          </div>
          <div className="flex items-center gap-2">
            {customer['dogma_region@OData.Community.Display.V1.FormattedValue'] && (
              <Tag color="blue">
                {customer['dogma_region@OData.Community.Display.V1.FormattedValue']}
              </Tag>
            )}
            {customer.statecode !== undefined && (
              <Tag color={getStatusColor(customer.statecode)} style={{ fontWeight: 500 }}>
                {customer['statecode@OData.Community.Display.V1.FormattedValue'] || 'Unknown'}
              </Tag>
            )}
          </div>
        </div>

        {/* Customer Details */}
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered>
          {/* Contact Email */}
          {(customer.primarycontactid?.emailaddress1 || customer.emailaddress1) && (
            <Descriptions.Item
              label={
                <span className="flex items-center gap-2">
                  <FaEnvelope className="text-gray-400" />
                  Contact Email
                </span>
              }
            >
              <a
                href={`mailto:${customer.primarycontactid?.emailaddress1 || customer.emailaddress1}`}
                className="text-blue-600 hover:text-blue-800"
              >
                {customer.primarycontactid?.emailaddress1 || customer.emailaddress1 || '-'}
              </a>
            </Descriptions.Item>
          )}

          {/* Phone */}
          {(customer.primarycontactid?.telephone1 || customer.telephone1) && (
            <Descriptions.Item
              label={
                <span className="flex items-center gap-2">
                  <FaPhone className="text-gray-400" />
                  Phone
                </span>
              }
            >
              <a
                href={`tel:${customer.primarycontactid?.telephone1 || customer.telephone1}`}
                className="text-blue-600 hover:text-blue-800"
              >
                {customer.primarycontactid?.telephone1 || customer.telephone1 || '-'}
              </a>
            </Descriptions.Item>
          )}

          {/* Address */}
          <Descriptions.Item
            label={
              <span className="flex items-center gap-2">
                <FaMapMarkerAlt className="text-gray-400" />
                Address
              </span>
            }
          >
            {customer.address1_composite || '-'}
          </Descriptions.Item>

          {/* Sage Number */}
          <Descriptions.Item label="Sage Number">
            {customer.dogma_prefix || '-'}
          </Descriptions.Item>

          {/* Customer Type */}
          <Descriptions.Item label="Customer Type">
            {customer['customertypecode@OData.Community.Display.V1.FormattedValue'] ? (
              <Tag>{customer['customertypecode@OData.Community.Display.V1.FormattedValue']}</Tag>
            ) : (
              '-'
            )}
          </Descriptions.Item>

          {/* Credit Terms */}
          <Descriptions.Item label="Credit Terms">
            {customer['paymenttermscode@OData.Community.Display.V1.FormattedValue'] || '-'}
          </Descriptions.Item>

          {/* Region */}
          <Descriptions.Item label="Region">
            {customer['dogma_region@OData.Community.Display.V1.FormattedValue'] || '-'}
          </Descriptions.Item>

          {/* Currency */}
          {dealersCurrency && (
            <Descriptions.Item label="Currency">
              {dealersCurrency.dogma_currencysymbol && (
                <Tag color="blue" className="mr-1">{dealersCurrency.dogma_currencysymbol}</Tag>
              )}
              {dealersCurrency.dogma_name} ({dealersCurrency.dogma_isocurrencycode})
            </Descriptions.Item>
          )}

          {/* Discount from VS Base Price */}
          {customer.dogma_discountpercentage != null && (
            <Descriptions.Item label="Discount %">
              <Tag color="green">{customer.dogma_discountpercentage}%</Tag>
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Credit summary — only for credit account customers */}
        {isCreditAccount && (customer.creditlimit != null || customer.dogma_availablecredit != null) && (
          <div className="flex gap-4 pt-2">
            {customer.creditlimit != null && (
              <div className="flex-1 rounded-lg bg-gray-50 border border-gray-200 p-3">
                <p className="text-xs text-gray-500 mb-1">Credit Limit</p>
                <p className="text-base font-semibold text-gray-900">{formatAmount(customer.creditlimit)}</p>
              </div>
            )}
            {customer.dogma_availablecredit != null && (
              <div className="flex-1 rounded-lg bg-gray-50 border border-gray-200 p-3">
                <p className="text-xs text-gray-500 mb-1">Available Credit</p>
                <p className={`text-base font-semibold ${customer.dogma_availablecredit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatAmount(customer.dogma_availablecredit)}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default CustomerDetailsHeader;
